import {
  PublicPageHead,
  PublicShareLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  API_ORIGIN,
  usePublicShareRouteParam,
} from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'

import { ConsultReportView } from './consult-report-view'
import type { ConsultReportPayload } from '../hooks/use-consult-reports'

interface PublicConsultReport {
  id: string
  visit_label: string
  visit_at: string | null
  payload: ConsultReportPayload
  created_at: string
}

export function PublicConsultReportPage({ tokenOverride }: { tokenOverride?: string } = {}) {
  const token = usePublicShareRouteParam(
    '/share/consult-report/:token',
    'token',
    tokenOverride,
  )
  const [report, setReport] = useState<PublicConsultReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [pdfBusy, setPdfBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!token) {
        if (!cancelled) {
          setLoading(false)
          setError('Report not found')
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/consultations/public/reports/${encodeURIComponent(token)}/`,
        )
        const raw = await response.text()
        let data: Record<string, unknown> = {}
        try {
          data = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
        } catch {
          throw new Error('Report could not be loaded.')
        }
        if (!response.ok) {
          throw new Error(
            typeof data.detail === 'string'
              ? data.detail
              : 'This consultation prep link is unavailable.',
          )
        }
        if (!cancelled) setReport(data as unknown as PublicConsultReport)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Report not found')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [token])

  const onPdf = async () => {
    if (!token) return
    setPdfBusy(true)
    try {
      const response = await fetch(
        `${API_ORIGIN}/api/consultations/public/reports/${encodeURIComponent(token)}/pdf/`,
      )
      if (!response.ok) throw new Error('Failed to download PDF')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `lumie-consult-prep.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download PDF')
    } finally {
      setPdfBusy(false)
    }
  }

  if (loading) {
    return (
      <PublicShareLayout>
        <Card>
          <CardHeader>
            <CardTitle>Loading consultation prep…</CardTitle>
          </CardHeader>
        </Card>
      </PublicShareLayout>
    )
  }

  if (error || !report) {
    return (
      <PublicShareLayout>
        <Card>
          <CardHeader>
            <CardTitle>Link unavailable</CardTitle>
            <CardDescription>
              {error || 'This consultation prep link could not be loaded.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </PublicShareLayout>
    )
  }

  const headline = report.payload?.headline || report.visit_label || 'Consultation preparation'

  return (
    <PublicShareLayout>
      <PublicPageHead title={`${headline} · Lumie`} description="Patient-prepared consultation pack" />
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle>Consultation prep</CardTitle>
            <CardDescription>Shared by a Lumie patient for an upcoming visit</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onPdf} disabled={pdfBusy}>
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
        </CardHeader>
        <CardContent>
          <ConsultReportView
            payload={report.payload}
            visitLabel={report.visit_label}
            visitAt={report.visit_at}
            showDisclaimerBanner
          />
        </CardContent>
      </Card>
    </PublicShareLayout>
  )
}
